// v2.12.0 — AppointmentModal : modale de prise de RDV (mock Doctolib-like).
// Affichée depuis HealthCard pour les profils type="medecin".
// POST /api/appointments/book → toast confirmation + close.

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function _empty() {
  return { full_name: "", phone: "", date: _todayISO(), time: "09:00", reason: "" };
}

export default function AppointmentModal({ open, onClose, profile, userId }) {
  const [form, setForm] = useState(_empty());
  const [loading, setLoading] = useState(false);
  if (!open || !profile) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("Nom + téléphone obligatoires");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/appointments/book`, {
        userId, profile_id: profile.id,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        date: form.date, time: form.time,
        reason: form.reason.trim() || null,
      });
      toast.success(`📅 RDV demandé — ${r.data.confirmation_message}`);
      setForm(_empty());
      onClose && onClose({ ok: true, booking_id: r.data.booking_id });
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur prise de RDV");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="appointment-modal"
      role="dialog"
      aria-modal="true"
      onClick={() => onClose && onClose({ ok: false, cancelled: true })}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f172a",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16, padding: 24,
          maxWidth: 460, width: "100%",
          color: "#fff",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ fontSize: 11, color: "#a78bfa", letterSpacing: "0.06em", marginBottom: 4, fontWeight: 700 }}>📅 PRISE DE RENDEZ-VOUS</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, lineHeight: 1.3 }}>Réserver avec {profile.nom}</h2>
        {profile.specialite && (
          <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>⚕️ {profile.specialite}</div>
        )}
        {profile.etablissement && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>🏥 {profile.etablissement}</div>
        )}

        <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
          <div>
            <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Nom complet *</label>
            <input
              type="text" value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="appt-field-name"
              placeholder="Ex: Awa Diabaté"
              style={{ width: "100%", padding: 9, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: 8, fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Téléphone *</label>
            <input
              type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="appt-field-phone"
              placeholder="+225 07 XX XX XX XX"
              style={{ width: "100%", padding: 9, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: 8, fontSize: 13 }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Date *</label>
              <input
                type="date" value={form.date}
                min={_todayISO()}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="appt-field-date"
                style={{ width: "100%", padding: 9, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: 8, fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Heure *</label>
              <input
                type="time" value={form.time} step={1800}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="appt-field-time"
                style={{ width: "100%", padding: 9, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: 8, fontSize: 13 }}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Motif (optionnel)</label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              data-testid="appt-field-reason"
              placeholder="Ex: consultation de suivi"
              rows={2}
              style={{ width: "100%", padding: 9, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: 8, fontSize: 13, resize: "vertical" }}
            />
          </div>
        </div>

        <div style={{ fontSize: 11, color: "#a78bfa", marginTop: 14, lineHeight: 1.5 }}>
          🚧 <strong>Démo</strong> : intégration Doctolib-like en cours. Le cabinet vous contactera dans les 24h pour confirmer.
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={() => onClose && onClose({ ok: false, cancelled: true })}
            data-testid="appt-cancel-btn"
            style={{ flex: 1, background: "rgba(255,255,255,0.08)", color: "#cbd5e1", border: "1px solid rgba(255,255,255,0.15)", padding: "10px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
          >Annuler</button>
          <button
            type="submit" disabled={loading}
            data-testid="appt-submit-btn"
            style={{ flex: 2, background: loading ? "#6B7280" : "#7C3AED", color: "#fff", border: "none", padding: "10px", borderRadius: 8, cursor: loading ? "wait" : "pointer", fontWeight: 800, fontSize: 13 }}
          >{loading ? "Envoi…" : "📅 Demander le RDV"}</button>
        </div>
      </form>
    </div>
  );
}
